import React from 'react';
import { connect } from 'react-redux';
import Select, { SingleValue, StylesConfig } from 'react-select';
import Loading from '../Loading';
import {
    getShows,
    getFilterOptions,
    selectors,
    setFilter,
} from '../../ducks/shows';
import { gettext } from '../../utils/text';
import SingleShow from './SingleShow';
import { Option, Show } from './types';
import DropdownIndicator from '../DropdownIndicator';

interface Filters {
    staging: Option;
    period: Option;
    stagingType: Option;
    upcomingShowsPageNumber: number;
    [key: string]: unknown;
}

interface HomepageWrapperProps {
    shows: Show[];
    filters: Filters;
    stagings: Option[];
    periods: Option[];
    stagingTypes: Option[];
    getFilterValue: (filter: string) => Option;
    getShows: (filters: Filters, reset?: boolean) => void;
    getFilterOptions: (filters: Filters) => void;
    applyFilter: (key: string, value: unknown) => void;
    initialLoading: boolean;
    nextLoading: boolean;
    nextPage: string | null;
}

interface HomepageWrapperState {
    pageNumber: number;
}

class HomepageWrapper extends React.Component<
    HomepageWrapperProps,
    HomepageWrapperState
> {
    showsContainerRef: React.RefObject<HTMLUListElement>;
    shouldFocusNewPageFirstItem: boolean;
    prevShowsLength: number;

    constructor(props: HomepageWrapperProps) {
        super(props);
        this.state = { pageNumber: 1 };
        this.showsContainerRef = React.createRef();
        this.shouldFocusNewPageFirstItem = false;
        this.prevShowsLength = 0;
    }

    componentDidMount() {
        this.props.getShows(this.props.filters);
        this.props.getFilterOptions(this.props.filters);
    }

    componentDidUpdate(prevProps: HomepageWrapperProps) {
        // When new shows are appended after clicking Next page, move focus
        if (
            this.shouldFocusNewPageFirstItem &&
            !this.props.nextLoading &&
            (prevProps.nextLoading ||
                this.props.shows.length > prevProps.shows.length)
        ) {
            const container = this.showsContainerRef.current;
            if (container) {
                const showElems = container.querySelectorAll('.__show');
                const focusIndex = this.prevShowsLength;
                if (showElems && showElems[focusIndex]) {
                    try {
                        const linkToFocus = showElems[focusIndex].querySelector(
                            'a',
                        );
                        if (linkToFocus) {
                            linkToFocus.focus();
                            linkToFocus.scrollIntoView({
                                block: 'nearest',
                            });
                        }
                    } catch (e) {
                        // no-op: focusing is best-effort
                    }
                }
            }
            this.shouldFocusNewPageFirstItem = false;
        }
    }

    onFilterChange = (filter: string, option: SingleValue<Option>) => {
        this.props.applyFilter(filter, option);
        const newFilter = {
            ...this.props.filters,
            upcomingShowsPageNumber: 1,
            [filter]: option,
        };
        // on filter change load shows from beginning
        this.props.getShows(newFilter, true);
        this.props.getFilterOptions(newFilter);
    };

    getDefaultCustomStyles = (): StylesConfig<Option> => ({
        control: provided => ({
            ...provided,
            border: '0 none',
            marginLeft: 40,
            marginTop: 1,
        }),
    });

    getNextPage = () => {
        const nextPageNumber = this.state.pageNumber + 1;
        this.setState({ pageNumber: nextPageNumber }, () => {
            this.prevShowsLength = this.props.shows.length;
            this.shouldFocusNewPageFirstItem = true;
            const newFilter = {
                ...this.props.filters,
                upcomingShowsPageNumber: this.state.pageNumber,
            };
            this.props.applyFilter(
                'upcomingShowsPageNumber',
                this.state.pageNumber,
            );
            this.props.getShows(newFilter);
        });
    };

    renderShows() {
        if (this.props.initialLoading) {
            return <Loading />;
        }
        let renderedShows: React.ReactNode = (
            <h2>{gettext('No results matching selected filters.')}</h2>
        );

        if (this.props.shows.length) {
            renderedShows = this.props.shows.map(show => {
                const key = `${show.id}-${show.staging.id}`;
                return <SingleShow show={show} key={key} />;
            });
        }

        return (
            <>
                <ul
                    className="__shows list-unstyled"
                    ref={this.showsContainerRef}
                >
                    {renderedShows}
                </ul>
                {this.renderUpcomingShowsNextButton()}
            </>
        );
    }

    renderUpcomingShowsNextButton = () => {
        if (this.props.nextLoading) {
            return <Loading />;
        }
        // Display the next page button only if the upcoming shows option is selected,
        // if the next page is present
        const showUpcomingShowsNextButton =
            this.props.getFilterValue('period').value === 1 &&
            this.props.nextPage;
        if (showUpcomingShowsNextButton) {
            return (
                <div className="__next-page-div">
                    <button
                        className="btn btn-primary "
                        type="button"
                        onClick={() => {
                            this.getNextPage();
                        }}
                    >
                        {gettext('Next page')}
                    </button>
                </div>
            );
        }
        return null;
    };

    renderStagingSelector() {
        return (
            <Select<Option>
                inputId="staging-filter-select"
                value={this.props.getFilterValue('staging')}
                onChange={option => this.onFilterChange('staging', option)}
                components={{ DropdownIndicator }}
                options={this.props.stagings}
                className="react-select menu-outer-top"
                classNamePrefix="react-select"
                styles={this.getDefaultCustomStyles()}
                isSearchable={false}
                aria-label={gettext('Filter by staging')}
                placeholder={gettext('Select staging')}
            />
        );
    }

    renderPeriodSelector() {
        return (
            <Select<Option>
                inputId="period-filter-select"
                value={this.props.getFilterValue('period')}
                onChange={option => this.onFilterChange('period', option)}
                components={{ DropdownIndicator }}
                options={this.props.periods}
                className="react-select"
                classNamePrefix="react-select"
                styles={this.getDefaultCustomStyles()}
                isSearchable={false}
                aria-label={gettext('Filter by period')}
                placeholder={gettext('Select period')}
            />
        );
    }

    renderStagingTypeSelector() {
        return (
            <Select<Option>
                inputId="staging-type-filter-select"
                value={this.props.getFilterValue('stagingType')}
                onChange={option => this.onFilterChange('stagingType', option)}
                components={{ DropdownIndicator }}
                options={this.props.stagingTypes}
                className="react-select"
                classNamePrefix="react-select"
                styles={this.getDefaultCustomStyles()}
                isSearchable={false}
                aria-label={gettext('Filter by staging type')}
                placeholder={gettext('Select staging type')}
            />
        );
    }

    render() {
        return (
            <>
                <div className="__filters">
                    <div className="__filter-group">
                        <label
                            className="__filter-label"
                            htmlFor="staging-filter-select"
                        >
                            {gettext('Staging')}
                        </label>
                        <div className="__filter __stagings">
                            {this.renderStagingSelector()}
                        </div>
                    </div>
                    <div className="__filter-group">
                        <label
                            className="__filter-label"
                            htmlFor="period-filter-select"
                        >
                            {gettext('Period')}
                        </label>
                        <div className="__filter __dates">
                            {this.renderPeriodSelector()}
                        </div>
                    </div>
                    <div className="__filter-group">
                        <label
                            className="__filter-label"
                            htmlFor="staging-type-filter-select"
                        >
                            {gettext('Staging type')}
                        </label>
                        <div className="__filter __scenes">
                            {this.renderStagingTypeSelector()}
                        </div>
                    </div>
                </div>
                {this.renderShows()}
            </>
        );
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (state: any) => ({
    shows: selectors.shows(state),
    filters: selectors.filters(state),
    stagings: selectors.stagings_options(state),
    periods: selectors.period_options(state),
    stagingTypes: selectors.staging_type_options(state),
    activeStagingSelection: selectors.staging(state),
    getFilterValue: (filter: string) =>
        selectors.getSelectedFilter(state, filter),
    initialLoading: state.shows.initialLoading,
    nextLoading: state.shows.nextLoading,
    nextPage: selectors.nextPage(state),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapDispatchToProps = (dispatch: any) => ({
    getShows: (filters: Filters, reset?: boolean) =>
        dispatch(getShows(filters, reset)),
    getFilterOptions: (filters: Filters) => dispatch(getFilterOptions(filters)),
    applyFilter: (key: string, value: unknown) => {
        dispatch(setFilter(key, value));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(HomepageWrapper);
